import React, { useState } from 'react'
import { Pencil, Flag, FileText, FilePlus, Bell, ArrowRight, Trash2 } from 'lucide-react'
import type { Todo } from '../../api/types'
import { Sheet } from '../primitives/Sheet'
import { PriorityPicker } from '../PriorityPicker'
import { ReminderOptions } from '../reminder/ReminderOptions'
import { useTodoActions } from '../../hooks/useTodoActions'

interface MobileTodoActionSheetProps {
  todo: Todo
  open: boolean
  onClose(): void
  onRequestEdit(): void
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '0 20px',
  height: 48,
  cursor: 'pointer',
  fontFamily: 'var(--font-serif)',
  fontSize: '1rem',
  color: 'var(--color-ink)',
  background: 'none',
  border: 'none',
  width: '100%',
  textAlign: 'left',
}

export function MobileTodoActionSheet({
  todo,
  open,
  onClose,
  onRequestEdit,
}: MobileTodoActionSheetProps) {
  const actions = useTodoActions(todo)
  const [showPriority, setShowPriority] = useState(false)
  const [showReminder, setShowReminder] = useState(false)

  const truncatedTitle =
    todo.text.length > 60 ? todo.text.slice(0, 60) + '…' : todo.text

  return (
    <Sheet open={open} onClose={onClose} title={truncatedTitle}>
      <div style={{ paddingBottom: 'calc(8px + env(safe-area-inset-bottom))' }}>

        {/* Edit text */}
        <button
          style={rowStyle}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => {
            onRequestEdit()
          }}
        >
          <Pencil size={16} strokeWidth={1.5} />
          Edit text
        </button>

        {/* Priority */}
        <button
          style={rowStyle}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => setShowPriority((v) => !v)}
        >
          <Flag size={16} strokeWidth={1.5} />
          Priority
        </button>
        {showPriority && (
          <div
            style={{ padding: '8px 20px 12px' }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <PriorityPicker
              todoId={todo.id}
              currentPriority={todo.priority}
              onApplied={() => {
                setShowPriority(false)
                onClose()
              }}
            />
          </div>
        )}

        {/* Add / Edit note */}
        <button
          style={rowStyle}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => {
            actions.openNote()
            onClose()
          }}
        >
          {todo.note ? (
            <FileText size={16} strokeWidth={1.5} />
          ) : (
            <FilePlus size={16} strokeWidth={1.5} />
          )}
          {todo.note ? 'Edit note' : 'Add note'}
        </button>

        {/* Set reminder */}
        <button
          style={rowStyle}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => setShowReminder((v) => !v)}
        >
          <Bell size={16} strokeWidth={1.5} />
          Set reminder
        </button>
        {showReminder && (
          <div
            style={{ padding: '4px 20px 8px' }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <ReminderOptions todo={todo} onDone={onClose} compact />
          </div>
        )}

        {/* Move to next day / Move to today */}
        <button
          style={rowStyle}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => {
            actions.moveToNextDay()
            onClose()
          }}
        >
          <ArrowRight size={16} strokeWidth={1.5} />
          {todo.date ? 'Move to next day' : 'Move to today'}
        </button>

        {/* Delete */}
        <button
          style={{ ...rowStyle, color: '#B43A2C' }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => {
            actions.deleteWithUndo()
            onClose()
          }}
        >
          <Trash2 size={16} strokeWidth={1.5} />
          Delete
        </button>
      </div>
    </Sheet>
  )
}
